import { Modal, ModalOpenButton, Select } from "@msys/ui";
import {
  Delete as DeleteIcon,
  Functions as FunctionsIcon,
} from "@mui/icons-material";
import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { EntitySearchFilterOperator } from "../../../../clients/graphqlTypes.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";

export interface SearchFilterOperatorRow {
  key: string;
  label: string;
  /** The filter's value alone — the operator has its own column. */
  value: string;
  isComputed: boolean;
  operator: EntitySearchFilterOperator;
  operatorOptions: { label: string; value: EntitySearchFilterOperator }[];
  /**
   * Persist a new comparison for this filter, applied immediately as the
   * properties manage modal applies its edits. Absent where the clause kind
   * follows the value rather than a user choice — the product context's named
   * filters, where one brand is an equality and several are a membership.
   */
  onChangeOperator?: (operator: EntitySearchFilterOperator) => Promise<void>;
  onDelete: () => Promise<void>;
}

interface Props {
  rows: SearchFilterOperatorRow[];
  /**
   * Wrap a computed row's delete affordance in the site's capability/permission
   * gate. Deleting a computed filter removes its expression with the slot, which
   * the merged mutation rejects without expression access.
   */
  renderExpressionGate?: (
    gated: React.ReactNode,
    fallback: React.ReactNode
  ) => React.ReactNode;
  handleClose: () => void;
}

const defaultGate = (gated: React.ReactNode) => gated;

/**
 * The context's search filters in one list: what each compares, and the two
 * edits that belong to the filter rather than to its value — the operator and
 * removing it. The section's own rows carry the value control alone, so
 * neither a column of selects nor a delete mode clutters them.
 */
export function SearchFilterOperatorsModal({
  rows,
  renderExpressionGate = defaultGate,
  handleClose,
}: Props) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  return (
    <Modal
      title={t("Edit search filters", { ns: "QuoteItem" })}
      dialogProps={{ maxWidth: "sm" }}
      handleClose={handleClose}
      actionButtons={[
        { label: t("Close", { ns: "Global" }), handleClick: handleClose },
      ]}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("Label", { ns: "Global" })}</TableCell>
            <TableCell>{t("Value", { ns: "Global" })}</TableCell>
            <TableCell sx={{ width: 150 }}>
              {t("Operator", { ns: "Global" })}
            </TableCell>
            <TableCell sx={{ width: 48 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => {
            const deleteButton = (
              <ModalOpenButton
                Modal={ConfirmModal}
                modalProps={{ handleConfirm: row.onDelete }}
              >
                <IconButton
                  color="primary"
                  size="small"
                  aria-label={t("Delete filter {label}", {
                    ns: "QuoteItem",
                    label: row.label,
                  })}
                >
                  <DeleteIcon />
                </IconButton>
              </ModalOpenButton>
            );

            return (
              <TableRow key={row.key}>
                <TableCell>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: "center" }}
                  >
                    {row.isComputed && (
                      <FunctionsIcon color="secondary" fontSize="small" />
                    )}
                    <Typography variant="body2">{row.label}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.value}</Typography>
                </TableCell>
                <TableCell>
                  {/* A computed filter's comparison belongs to its formula and
                    is changed there, alongside the expression. */}
                  {row.isComputed ||
                  !row.onChangeOperator ||
                  row.operatorOptions.length < 2 ? (
                    <Typography variant="body2">
                      {row.operatorOptions.find(o => o.value === row.operator)
                        ?.label ?? row.operator}
                    </Typography>
                  ) : (
                    <Select
                      label={""}
                      size="extra-small"
                      options={row.operatorOptions}
                      value={row.operator}
                      inputProps={{
                        "aria-label": t("Operator for {label}", {
                          ns: "QuoteItem",
                          label: row.label,
                        }),
                      }}
                      onChange={(next: EntitySearchFilterOperator) =>
                        void row.onChangeOperator?.(next)
                      }
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  {row.isComputed
                    ? renderExpressionGate(deleteButton, null)
                    : deleteButton}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Modal>
  );
}
